import { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { ServiceDto, ServiceListDto } from '../general/dtos';
import { useApi } from './api-provider';
import { useBookmarkSelection } from '../general/bookmark-selection';

interface ServicesContextValue {
    services: ServiceListDto[];
    selectedKey: string | null;
    selectService: (key: string) => void;
    service: ServiceDto | null;
    loading: boolean;
    error: string;
    detailLoading: boolean;
    detailError: string;
    reloadList: () => void;
    reloadDetail: () => void;
}

const ServicesContext = createContext<ServicesContextValue | null>(null);

export function useServices() {
    const context = useContext(ServicesContext);
    if (!context) throw new Error('useServices must be used inside ServicesDataProvider');
    return context;
}

export function ServicesDataProvider({ children }: { children: React.ReactNode }) {
    const api = useApi();
    const [services, setServices] = useState<ServiceListDto[]>([]);
    const [selectedKey, setSelectedKey] = useBookmarkSelection('service');
    const [service, setService] = useState<ServiceDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');
    const [listRevision, setListRevision] = useState(0);
    const [detailRevision, setDetailRevision] = useState(0);

    useEffect(() => {
        if (!api) return;
        const controller = new AbortController();
        setLoading(true);
        setError('');
        api.getServices(controller.signal).then(data => {
            if (!controller.signal.aborted) setServices(data);
        }).catch(error => {
            if (!controller.signal.aborted && !axios.isCancel(error)) setError('Could not load services. Please try again.');
        }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [api, listRevision]);

    useEffect(() => {
        const controller = new AbortController();
        setService(null);
        setDetailError('');
        setDetailLoading(false);
        if (!api || !selectedKey) return;
        setDetailLoading(true);
        api.getService(selectedKey, controller.signal).then(data => {
            if (!controller.signal.aborted) setService(data);
        }).catch(error => {
            if (!controller.signal.aborted && !axios.isCancel(error)) setDetailError('Could not load this service. Please try again.');
        }).finally(() => { if (!controller.signal.aborted) setDetailLoading(false); });
        return () => controller.abort();
    }, [api, selectedKey, detailRevision]);

    return <ServicesContext.Provider value={{ services, selectedKey, selectService: setSelectedKey, service,
        loading, error, detailLoading, detailError,
        reloadList: () => setListRevision(value => value + 1), reloadDetail: () => setDetailRevision(value => value + 1) }}>
        {children}
    </ServicesContext.Provider>;
}
